"use client";

import { useEffect, useState, useMemo, useCallback } from "react";
import { createBrowserClient } from "@/lib/supabase/client";
import { AuthContext } from "@/hooks/auth/useAuth";
import { AuthState, AuthUser, User } from "@/types/auth";
import { UserAccountWithAccount } from "@/types/accounts";
import { Warehouse } from "@/types/warehouse";
import { useRouter } from "next/navigation";

const createUserObject = async (user: User, supabase: ReturnType<typeof createBrowserClient>): Promise<AuthUser> => {
  let accounts: UserAccountWithAccount[] = [];
  let warehouse_id: string | null = null;
  let warehouse_timezone: string | null = null;
  let can_access_all_warehouses = false;
  let warehouses: Warehouse[] | undefined;

  try {
    const { data: userAccountsData, error: userAccountsError } = await supabase
      .from("user_accounts")
      .select("*, accounts!inner(id, name, slug, is_active)")
      .eq("user_id", user.id);

    if (userAccountsError) {
      console.error("❌ Error fetching user accounts:", userAccountsError);
      throw userAccountsError;
    }

    accounts = userAccountsData || [];

    // Load warehouse assignment from first account
    if (accounts.length > 0) {
      const firstAccount = accounts[0];
      warehouse_id = firstAccount.warehouse_id || null;
      can_access_all_warehouses = firstAccount.can_access_all_warehouses || false;

      // If super user, load all available warehouses (global)
      if (can_access_all_warehouses) {
        const { data: warehousesData, error: warehousesError } = await supabase
          .from("warehouses")
          .select("*")
          .eq("is_active", true)
          .order("name");

        if (!warehousesError && warehousesData) {
          warehouses = warehousesData;

          // Check localStorage for warehouse preference
          const storedWarehouseId = localStorage.getItem(`selected_warehouse_${user.id}`);
          if (storedWarehouseId && warehouses.some(w => w.id === storedWarehouseId)) {
            warehouse_id = storedWarehouseId;
            // Get timezone for selected warehouse
            const selectedWarehouse = warehouses.find(w => w.id === storedWarehouseId);
            warehouse_timezone = selectedWarehouse?.timezone || null;
          } else if (!warehouse_id && warehouses.length > 0) {
            // Default to first warehouse if no preference
            warehouse_id = warehouses[0].id;
            warehouse_timezone = warehouses[0].timezone || null;
          }
        }
      } else if (warehouse_id) {
        // Regular user - fetch their warehouse timezone
        const { data: warehouseData } = await supabase
          .from("warehouses")
          .select("timezone")
          .eq("id", warehouse_id)
          .single();
        warehouse_timezone = warehouseData?.timezone || null;
      }
    }

    return {
      id: user.id,
      email: user.email || "",
      name:
        user.user_metadata.display_name ||
        `${user.user_metadata.first_name || ""} ${user.user_metadata.last_name || ""}`.trim() ||
        user.email ||
        "Unknown User",
      phone: user.phone || "",
      user_metadata: user.user_metadata,
      accounts,
      warehouse_id,
      warehouse_timezone,
      can_access_all_warehouses,
      warehouses,
    };
  } catch (error) {
    console.error("❌ Error fetching user accounts:", error);
    return {
      id: user.id,
      email: user.email || "",
      name:
        user.user_metadata.display_name ||
        `${user.user_metadata.first_name || ""} ${user.user_metadata.last_name || ""}`.trim() ||
        user.email ||
        "Unknown User",
      phone: user.phone || "",
      user_metadata: user.user_metadata,
      accounts,
      warehouse_id: null,
      warehouse_timezone: null,
      can_access_all_warehouses: false,
    };
  }
};

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    loading: true,
    isAuthenticated: false,
  });

  const router = useRouter();
  const supabase = useMemo(() => createBrowserClient(), []);

  // Initialize auth state
  useEffect(() => {
    const initAuth = async () => {
      try {
        setAuthState((prev) => ({ ...prev, loading: true }));

        // Get initial session
        const {
          data: { session },
        } = await supabase.auth.getSession();

        if (session?.user) {
          const user = await createUserObject(session.user, supabase);
          setAuthState({ user, loading: false, isAuthenticated: true });
        } else {
          setAuthState({ user: null, loading: false, isAuthenticated: false });
        }
      } catch (error) {
        console.error("❌ Auth initialization error:", error);
        setAuthState({ user: null, loading: false, isAuthenticated: false });
      }
    };

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log("🔄 Auth state changed:", event);

      try {
        if (event === "SIGNED_IN" && session?.user) {
          setTimeout(async () => {
            const user = await createUserObject(session.user, supabase);
            setAuthState({ user, loading: false, isAuthenticated: true });
          }, 0);
        } else if (event === "SIGNED_OUT") {
          setAuthState({ user: null, loading: false, isAuthenticated: false });
          // Immediately redirect to login
          router.push("/login");
        } else if (event === "USER_UPDATED" && session) {
          setTimeout(async () => {
            console.log("update user session");
            const user = await createUserObject(session.user, supabase);
            setAuthState({ user, loading: false, isAuthenticated: true });
          }, 0);
        }
      } catch (error) {
        console.error("❌ Error handling auth state change:", error);
        setAuthState((prev) => ({ ...prev, loading: false }));
      }
    });

    initAuth();

    return () => {
      subscription?.unsubscribe();
    };
  }, [supabase, router]);

  const signIn = useCallback(async (email: string, password: string): Promise<{ error?: string }> => {
    setAuthState((prev) => ({ ...prev, loading: true }));
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      setAuthState((prev) => ({ ...prev, loading: false }));
      return { error: error.message };
    }

    // Success - user will be set by the auth state change listener
    return {};
  }, []);

  const signOut = useCallback(async () => {
    try {
      // Check if there's an active session before attempting sign out
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session) {
        // No active session, just clear local state and redirect
        console.log("No active session to sign out from");
        setAuthState({ user: null, loading: false, isAuthenticated: false });
        router.push("/login");
        return;
      }

      const { error } = await supabase.auth.signOut();
      if (error) {
        // If error is about missing session, handle gracefully
        if (error.message?.includes("session") || error.message?.includes("Session")) {
          console.log("Session already cleared");
          setAuthState({ user: null, loading: false, isAuthenticated: false });
          router.push("/login");
          return;
        }
        throw error;
      }
      // User will be cleared and redirect will happen in auth state change listener
    } catch (error) {
      console.error("Sign out error:", error);
      // Even if sign out fails, clear local state
      setAuthState({ user: null, loading: false, isAuthenticated: false });
      router.push("/login");
    }
  }, [router]);

  const updateUser = useCallback(async (first_name: string, last_name: string) => {
    const { error } = await supabase.auth.updateUser({
      data: {
        first_name,
        last_name,
        display_name: `${first_name} ${last_name}`.trim(),
      },
    });

    if (error) {
      console.error("❌ Error updating user metadata:", error);
      throw error;
    }
  }, []);

  const switchWarehouse = useCallback(async (warehouseId: string) => {
    if (!authState.user?.can_access_all_warehouses) {
      throw new Error("Only super users can switch warehouses");
    }

    if (!authState.user.warehouses?.some(w => w.id === warehouseId)) {
      throw new Error("Invalid warehouse ID");
    }

    // Store preference in localStorage
    localStorage.setItem(`selected_warehouse_${authState.user.id}`, warehouseId);

    // Update user context
    setAuthState(prev => ({
      ...prev,
      user: prev.user ? { ...prev.user, warehouse_id: warehouseId } : null,
    }));

    // Reload page to refresh data with new warehouse context
    window.location.reload();
  }, [authState.user]);

  const contextValue = useMemo(
    () => ({
      user: authState.user,
      loading: authState.loading,
      isAuthenticated: authState.isAuthenticated,
      signIn,
      signOut,
      updateUser,
      switchWarehouse,
    }),
    [authState, signIn, signOut, updateUser, switchWarehouse]
  );

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
}
